// HOSTILE-flagged comments surface for the admin Report Summary (v6.5,
// 2026-05-24). Lists every submit-trigger comment where the AI safety
// screen returned HOSTILE or the rule engine marked isHostile=true.
//
// Pulls from GET /api/survey/:surveyId/report/hostile.

import React, { useEffect, useState } from 'react';
import {
  Alert, Badge, Card, Spinner,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import { getSurveyHostileSubmissions } from '../../../bridge';
import { logError } from '../../../helpers/logger';

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
};

const fullName = (p) => {
  if (!p) return 'Unknown';
  const fn = (p.firstName || '').trim();
  const ln = (p.lastName || '').trim();
  const name = [fn, ln].filter(Boolean).join(' ');
  return name || p.email || p.respondentId || 'Unknown';
};

export function HostileCommentsAdmin({ surveyId }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (!surveyId) return undefined;
    let cancelled = false;
    setLoading(true);
    setErrMsg('');
    getSurveyHostileSubmissions(surveyId)
      .then(({ response }) => {
        if (cancelled) return;
        setItems((response && response.hostile) || []);
      })
      .catch((err) => {
        if (cancelled) return;
        logError('getSurveyHostileSubmissions', err);
        setErrMsg('Unable to load flagged comments.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [surveyId]);

  return (
    <Card className="pagebreak py-3 my-3 border-warning">
      <Card.Body>
        <h3 className="mb-3">
          Flagged comments
          {items ? (
            <Badge bg={items.length === 0 ? 'success' : 'warning'} className="ms-2">
              {items.length}
            </Badge>
          ) : null}
        </h3>
        <p className="text-muted small">
          Submitted peer-feedback comments that the AI safety screen
          flagged as HOSTILE at submit time. These are visible to the
          survey administrator only; the writer was warned but chose to
          submit anyway.
        </p>
        {loading ? (
          <div className="my-3">
            <Spinner animation="border" role="status" size="sm" className="me-2" />
            <span>Loading flagged comments...</span>
          </div>
        ) : null}
        {errMsg ? <Alert variant="warning">{errMsg}</Alert> : null}
        {!loading && !errMsg && items && items.length === 0 ? (
          <Alert variant="success" className="mb-0">
            No HOSTILE-flagged comments were submitted in this survey.
          </Alert>
        ) : null}
        {!loading && items && items.length > 0 ? (
          <div>
            {items.map((it) => (
              <div
                key={String(it._id)}
                className="border-start border-3 border-danger ps-3 my-3"
              >
                <div className="d-flex justify-content-between text-muted small">
                  <span>
                    {`From ${fullName(it.rater)}`}
                    {it.rater && it.rater.team ? ` (Team ${it.rater.team})` : ''}
                    {' '}
                    {`about ${fullName(it.target)}`}
                  </span>
                  <span>{formatDate(it.createdAt)}</span>
                </div>
                <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                  {it.inputText}
                </div>
                {it.aiCoaching ? (
                  <div className="mt-2 small text-muted fst-italic">
                    AI coaching shown to writer:&nbsp;
                    {it.aiCoaching}
                  </div>
                ) : null}
                <div className="mt-1 small">
                  <Badge bg="danger" className="me-2">{it.aiScreenLabel || 'HOSTILE'}</Badge>
                  {it.wordCount ? <span className="text-muted">{`${it.wordCount} words`}</span> : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

HostileCommentsAdmin.propTypes = {
  surveyId: PropTypes.string.isRequired,
};

export default HostileCommentsAdmin;
